import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/ArrowDown.svg';

import { useAppSelector } from 'modules/state-mgmt/store';
import { mustCreateWorkerPassword } from 'utils/onboarding';
import { ROUTES } from 'constants/routes';
import { useStyles } from './styles';

export enum NavigationOption {
  MY_PROFILE = 'MY_PROFILE',
  MY_PROJECTS = 'MY_PROJECTS',
  MY_CERTIFICATIONS = 'MY_CERTIFICATIONS',
  MY_COURSES = 'MY_COURSES',
  MY_DRUG_TESTING = 'MY_DRUG_TESTING',
}

const navigationItems = [
  {
    option: NavigationOption.MY_PROFILE,
    path: ROUTES.MY_PROFILE.path,
    translationKey: 'myProfile.title',
    testId: 'nav-dropdown-item-my-profile',
  },
  {
    option: NavigationOption.MY_PROJECTS,
    path: ROUTES.PROJECT_LIST.path,
    translationKey: 'projectList.title',
    testId: 'nav-dropdown-item-my-projects',
  },
  {
    option: NavigationOption.MY_CERTIFICATIONS,
    path: ROUTES.MY_CERTIFICATIONS.path,
    translationKey: 'myCertifications.title',
    testId: 'nav-dropdown-item-my-certifications',
  },
  {
    option: NavigationOption.MY_COURSES,
    path: ROUTES.MY_COURSES.path,
    translationKey: 'courses.title',
    testId: 'nav-dropdown-item-my-courses',
  },
  {
    option: NavigationOption.MY_DRUG_TESTING,
    path: ROUTES.MY_DRUG_TESTING.path,
    translationKey: 'myDrugTesting.title',
    testId: 'nav-dropdown-item-my-drug-testing',
  },
];

export interface INavigationDropdownProps {
  excludeOptions?: NavigationOption[];
}

const NavigationDropdown: React.FC<INavigationDropdownProps> = ({ excludeOptions = [] }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const { isWorker, isDrugTestingAvailable, mustCreatePassword } = useAppSelector(state => ({
    isWorker: state.user?.isWorker,
    isDrugTestingAvailable: state.onboarding?.isDrugTestingAvailable,
    mustCreatePassword: mustCreateWorkerPassword(state.onboarding),
  }));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    handleClose();
    if (!isWorker && !navigator.onLine) {
      enqueueSnackbar(t('common.stableInternetRequired'), { variant: 'error' });
    } else {
      navigate(path);
    }
  };

  const filteredItems = navigationItems.filter(item => {
    if (item.option === NavigationOption.MY_DRUG_TESTING && !isDrugTestingAvailable) return false;
    return !excludeOptions.includes(item.option);
  });

  // Every destination here is a worker-facing screen that the route guard blocks while an
  // OTP-verified worker still has to create their password, so render no navigation at all
  // rather than a menu whose every item bounces straight back (CH-4515).
  if (mustCreatePassword) {
    return null;
  }

  return (
    <>
      <Button
        className={classes.accountButton}
        data-testid="nav-dropdown-button"
        onClick={handleOpen}
        disableRipple={true}
        endIcon={<ArrowDownIcon className={isOpen ? classes.arrowIconRotated : classes.arrowIcon} />}
      >
        {t('common.myAccount')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        className={classes.contextMenu}
        disableScrollLock={true}
        disableRestoreFocus={true}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredItems.map(item => (
          <MenuItem
            key={item.option}
            data-testid={item.testId}
            onClick={() => handleNavigate(item.path)}
            className={classes.menuItem}
          >
            {t(item.translationKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NavigationDropdown;
